import type { PDFDocumentProxy } from "pdfjs-dist";
import { pdfjs } from "./pdfjs";
import { MM_PER_IN, MM_PER_PT } from "./units";
import type { Source, SourcePdf, SourcePng } from "./types";

const PREVIEW_MAX_PX = 3000;
const PREVIEW_MAX_SCALE = 4;

export const isPdfFile = (file: File): boolean =>
  /\.pdf$/i.test(file.name) || file.type === "application/pdf";

export const isPngFile = (file: File): boolean =>
  /\.png$/i.test(file.name) || file.type === "image/png";

export const loadPng = (file: File): Promise<SourcePng> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const widthPx = img.naturalWidth;
      const heightPx = img.naturalHeight;
      resolve({
        type: "png",
        name: file.name,
        image: img,
        widthPx,
        heightPx,
        nativeWidthMm:  (widthPx  * MM_PER_IN) / 96,
        nativeHeightMm: (heightPx * MM_PER_IN) / 96,
      });
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });

export const loadPdf = async (file: File): Promise<PDFDocumentProxy> => {
  const buf = await file.arrayBuffer();
  return await pdfjs.getDocument({ data: buf }).promise;
};

export const renderPdfPage = async (
  pdfDoc: PDFDocumentProxy,
  pageNum: number,
): Promise<Omit<SourcePdf, "type" | "name" | "pdfDoc">> => {
  const page = await pdfDoc.getPage(pageNum);
  const baseVp = page.getViewport({ scale: 1 });
  const scale = Math.min(PREVIEW_MAX_PX / Math.max(baseVp.width, baseVp.height), PREVIEW_MAX_SCALE);
  const vp = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(vp.width);
  canvas.height = Math.ceil(vp.height);
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({ canvasContext: ctx, viewport: vp }).promise;
  return {
    pageNum,
    image: canvas,
    widthPx: canvas.width,
    heightPx: canvas.height,
    nativeWidthMm:  baseVp.width  * MM_PER_PT,
    nativeHeightMm: baseVp.height * MM_PER_PT,
  };
};

export const renderPdfThumb = async (
  pdfDoc: PDFDocumentProxy,
  pageNum: number,
  scale = 0.3,
): Promise<HTMLCanvasElement> => {
  const page = await pdfDoc.getPage(pageNum);
  const vp = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  canvas.width = vp.width;
  canvas.height = vp.height;
  const ctx = canvas.getContext("2d")!;
  await page.render({ canvasContext: ctx, viewport: vp }).promise;
  return canvas;
};

export const sourceImage = (s: Source): CanvasImageSource => s.image;
